import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { SafeHaptics } from '@euler/utils/haptics';
import { useEffect, useMemo, useRef } from 'react';
import { Keyboard, PanResponder, useWindowDimensions } from 'react-native';
import { BehaviorSubject } from 'rxjs';
import { HoldZone, holdZoneAt, kHoldArcHeight } from './holdZone';
import { VoiceTurn } from './useVoiceTurn';

/** A press held this long is a hold: talking starts, under the overlay. */
const kHoldMs = 300;
/** A hold sends until the finger has moved this far. */
const kMinMove = 24;

/**
 * A surface that talks while held, as in WeChat: held, the overlay opens
 * and the voice is captured; letting go sends it, or cancels or opens it
 * to edit as the finger moved up onto 取消 or 编辑. A tap is `onTap`.
 */
export function useHoldToTalk({
  voice,
  zone$,
  canTalk,
  onTap,
}: {
  voice: VoiceTurn;
  zone$: BehaviorSubject<HoldZone>;
  canTalk: boolean;
  onTap: () => void;
}) {
  const { rpx } = useResponsiveContext();
  const window = useWindowDimensions();
  // The handlers read the latest values without rebuilding the responder.
  const latest = useRef({ voice, zone$, canTalk, onTap, window, rpx });
  latest.current = { voice, zone$, canTalk, onTap, window, rpx };
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holding = useRef(false);
  const clearTimer = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  // Unmounted mid-hold, nothing will let go of it: it is dropped.
  useEffect(
    () => () => {
      clearTimer();
      if (holding.current) latest.current.voice.cancel();
    },
    [],
  );
  return useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onPanResponderTerminationRequest: () => !holding.current,
        onPanResponderGrant: () => {
          holding.current = false;
          clearTimer();
          if (!latest.current.canTalk) return;
          timer.current = setTimeout(() => {
            timer.current = null;
            if (
              !latest.current.canTalk ||
              latest.current.voice.state$.getValue().kind !== 'idle'
            )
              return;
            holding.current = true;
            Keyboard.dismiss();
            latest.current.zone$.next('send');
            SafeHaptics.selection();
            latest.current.voice.press('hold');
          }, kHoldMs);
        },
        onPanResponderMove: (_, gesture) => {
          if (!holding.current) return;
          const { window: size, rpx: scale } = latest.current;
          const zone = holdZoneAt({
            x: gesture.moveX,
            y: gesture.moveY,
            moved: Math.hypot(gesture.dx, gesture.dy),
            width: size.width,
            height: size.height,
            arcHeight: scale(kHoldArcHeight),
            minMove: scale(kMinMove),
          });
          if (zone === latest.current.zone$.getValue()) return;
          latest.current.zone$.next(zone);
          SafeHaptics.selection();
        },
        onPanResponderRelease: () => {
          clearTimer();
          if (!holding.current) {
            latest.current.onTap();
            return;
          }
          holding.current = false;
          const { voice: turn } = latest.current;
          const zone = latest.current.zone$.getValue();
          if (zone === 'cancel') turn.cancel();
          else turn.release(zone);
        },
        onPanResponderTerminate: () => {
          clearTimer();
          if (!holding.current) return;
          holding.current = false;
          latest.current.voice.cancel();
        },
      }),
    [],
  );
}
